import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import { Button, Text, H3, H4, Panel, Dropdown, Small, Tabs, Box, Select } from '@bigcommerce/big-design';
import { ArrowDropDownIcon, AddIcon } from '@bigcommerce/big-design-icons';

import CreateSupplierAssignments from './CreateSupplierAssignments';
import ViewSupplierAssignments from './ViewSupplierAssignments';
import UploadSupplierAssignments from './UploadSupplierAssignments';

import { suppliersIndex } from '../../redux/actions/suppliers';
import { reset } from '../../redux/actions/assignments';

const Assignments = ({ payload, suppliers, loadSuppliers, resetAssignments }) => {
  const [activeSupplier, setActiveSupplier] = useState(null);
  const [activeTab, setActiveTab] = useState('view');

  useEffect(() => {
    loadSuppliers(payload);
  }, []);

  const setDropdownValue = (setField, value) => {
    setField(value);
  }

  const supplierSelectItems = () => {
    let items = [];

    suppliers.suppliers.map(supplier => 
      items.push(
        {
          value: supplier,
          content: supplier.supplier_name
        }
      )
    )

    return items;
  }

  const tabItems = [
    { ariaControls: 'content1', id: 'view', title: 'View Assignments' },
    { ariaControls: 'content2', id: 'create', title: 'Search/Manage Assignments' },
    { ariaControls: 'content3', id: 'upload', title: 'Upload Assignments CSV' }
  ];

  const clearSupplier = () => {
    setActiveSupplier(null);
    resetAssignments();
  }

  const description = () => {
    return(
      <> 
        <Text>Assign products from your BigCommerce store to suppliers. Start by selecting a supplier from the "Select Supplier" dropdown. If the supplier you're looking for is missing, you can manage suppliers on the <Link to="/suppliers">Suppliers page</Link>.</Text>
        <Text>Assigned products let you track a supplier SKU, the supplier's price for this item, and a minimum order quantity as required by the supplier.</Text>
        <Text>When a supplier is selected, you can view, update and remove assigned products from the "View Assignments" tab. Page through assignments in sets of 10 by clicking the "Previous Assignments" or "Next Assignments" buttons.</Text>
        <Text>
          For creating new assignments, go to the "Search/Manage Assignments" tab. 
          Search for the products you want to assign to this supplier by typing your search into the search bar, and seeing the products found below. 
          For products without assignments, you'll see fields to set the supplier price, minimum order quantity and the supplier's sku for the product. 
          For products with existing assignments for this supplier, you'll see options to edit or remove the assignment.
        </Text>
        <Text>
          When you have the settings input for supplier prices, skus and order quantities, make sure that the products you want to have assignments created for have been checked off, and click the "Submit New Product Assignments" button to create new assignments for this supplier.
        </Text>
        <Text>
          Upload a CSV with supplier assignment information in the Upload Assignments CSV tab.
        </Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#fbe194' helpLink="bigcommerce/manage-supplier-product-assignments/" />
      <SettingsPage title="Supplier Product Assignments" description={description()}>
        <Panel>
          <H3>Suppliers</H3>
          { suppliers.isFetching &&
            <LoadingSpinner statusText="Loading Suppliers" />
          }

          { suppliers.status === 'success' && !activeSupplier && suppliers.suppliers.length > 0 &&
            <Select
              filterable={true}
              label="Suppliers"
              maxHeight={300}
              onOptionChange={(supplier) => setActiveSupplier(supplier)}
              options={supplierSelectItems()}
              placeholder="Select Supplier"
              placement="bottom-start"
              required
              value={activeSupplier?.supplier_name}
            />
          }

          { suppliers.suppliers.length === 0 && !suppliers.isFetching &&
            <Box
              backgroundColor="warning10"
              border="box"
              borderRadius="normal"
              padding="medium"
            >
              <div className="text-center">
                <Text>No suppliers available. Add supplier information to see suppliers in a dropdown list here.</Text>
                <Link to="/suppliers">
                  <Button iconLeft={<AddIcon />}>
                    Manage Suppliers
                  </Button>
                </Link>
              </div>
            </Box>
          }

          { activeSupplier && 
            <>
              <H4>{ activeSupplier.supplier_name }</H4>
              <Button onClick={() => clearSupplier()} variant="secondary" actionType="destructive">Clear supplier</Button>
              <hr/>
              <Tabs
                activeTab={activeTab}
                aria-label="Supplier Option Tabs"
                id="tab-example"
                items={tabItems}
                onTabClick={setActiveTab}
              />
              { activeTab === 'view' &&
                <ViewSupplierAssignments
                  payload={payload}
                  activeSupplier={activeSupplier}
                />
              }
              { activeTab === 'create' &&
                <CreateSupplierAssignments
                  payload={payload}
                  activeSupplier={activeSupplier}
                  setActiveTab={setActiveTab}
                />
              }
              { activeTab === 'upload' &&
                <UploadSupplierAssignments
                  payload={payload}
                  activeSupplier={activeSupplier}
                />
              }
            </>
          }
        </Panel>
      </SettingsPage>
    </>
  )

}

const mapStateToProps = state => ({
  suppliers: state.suppliers,
});

const mapDispatchToProps = (dispatch) => ({
  loadSuppliers: (payload) => dispatch(suppliersIndex(payload)),
  resetAssignments: () => dispatch(reset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Assignments);